import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, createRef, useState, useImperativeHandle, useEffect } from "react";
import dayjs from "dayjs";
import EzHeading from "../EzHeading/EzHeading.es.js";
import EzTextStyle from "../EzTextStyle/EzTextStyle.es.js";
import stitches from "../theme.config.es.js";
import en from "./en.es.js";
import EzButton from "../EzButton/EzButton.es.js";
import useTranslation from "../../utils/hooks/useTranslation.es.js";
const container = stitches.css({
  width: "$full",
  textAlign: "center",
  button: {
    fontSize: "inherit"
  },
  "&& > * + *": {
    marginTop: "$75"
  }
});
const dayOfWeek = stitches.css({
  fontWeight: "$calendar-day-of-week",
  textDecoration: "none"
});
const row = stitches.css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
});
const pagination = stitches.css({
  padding: "$100"
});
const cell = stitches.css({
  padding: "$100",
  // normalize the sizes of the cells
  flex: 1,
  variants: {
    selected: {
      true: {
        borderRadius: "$regular",
        border: "solid 1px $calendar-day-border-selected",
        backgroundColor: "$calendar-day-bg-selected"
      }
    }
  }
});
const dayValue = stitches.css({
  backgroundColor: "transparent",
  border: 0,
  color: "$calendar-day-text",
  cursor: "pointer",
  fontFamily: "$defaultFont",
  fontWeight: 700,
  padding: 0,
  "&:hover": {
    color: "$calendar-day-text-dark"
  },
  "&:disabled": {
    opacity: "50%"
  }
});
const weekDayCount = 7;
const maxDaysInMonth = 31;
const repeat = (n) => new Array(n).fill(null);
const populateMonth = (date) => {
  const daysInMonth = date.endOf("month").date();
  const monthDayStartOffset = date.set("date", 1).day() + 1;
  const weeksInMonth = Math.ceil((monthDayStartOffset + daysInMonth) / weekDayCount);
  let tempMonthDayStartOffset = monthDayStartOffset;
  let daysLeftInMonth = date.endOf("month").date();
  return repeat(weeksInMonth).map(
    () => repeat(weekDayCount).map(() => {
      if (--tempMonthDayStartOffset > 0) return null;
      const day = daysInMonth - --daysLeftInMonth;
      if (day <= daysInMonth) return day;
      return null;
    })
  );
};
const EzCalendar = ({ value, onChange, minDate, maxDate, filterDate }, ref) => {
  const { t } = useTranslation(en);
  const calendarRef = useRef(null);
  const refs = useRef(repeat(maxDaysInMonth).map(() => createRef())).current;
  const selectedDate = dayjs(value).isValid() ? dayjs(value) : dayjs();
  const [focusedDate, setFocusedDate] = useState(selectedDate);
  const focusTarget = useRef();
  useImperativeHandle(ref, () => ({
    focus() {
      focusTarget.current.focus();
    }
  }));
  useEffect(() => {
    const { activeElement, body } = calendarRef.current.ownerDocument;
    const bodyFocused = activeElement && activeElement === body;
    const { current } = refs[focusedDate.date() - 1];
    const dateFocused = refs.some((el) => el.current === activeElement);
    const canFocus = current && (bodyFocused || dateFocused);
    if (canFocus) current.focus();
  }, [refs, focusedDate, value]);
  useEffect(() => {
    if (dayjs(value).isValid()) setFocusedDate(dayjs(value));
  }, [value]);
  const handleKeyInput = (selectFocusedDate, disabled) => (e) => {
    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        setFocusedDate(focusedDate.subtract(1, "day"));
        break;
      case "ArrowRight":
        e.preventDefault();
        setFocusedDate(focusedDate.add(1, "day"));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedDate(focusedDate.subtract(1, "week"));
        break;
      case "ArrowDown":
        e.preventDefault();
        setFocusedDate(focusedDate.add(1, "week"));
        break;
      case "Space":
      case "Enter":
        e.preventDefault();
        if (!disabled) selectFocusedDate();
        break;
    }
  };
  const isEnabled = (day) => {
    const isSameOrAfterMinDate = minDate !== void 0 ? day.isSame(minDate) || day.isAfter(minDate) : true;
    const isSameOrBeforeMaxDate = maxDate !== void 0 ? day.isSame(maxDate) || day.isBefore(maxDate) : true;
    const isFilterMatch = filterDate !== void 0 ? filterDate(day.format(t("DATE_FORMAT"))) : true;
    return isSameOrAfterMinDate && isSameOrBeforeMaxDate && isFilterMatch;
  };
  return /* @__PURE__ */ jsxs("section", { className: container(), ref: calendarRef, children: [
    /* @__PURE__ */ jsxs("div", { className: row(), children: [
      /* @__PURE__ */ jsx("div", { className: pagination(), children: /* @__PURE__ */ jsxs(
        EzButton,
        {
          color: "primary",
          onClick: () => setFocusedDate(focusedDate.subtract(1, "month").set("date", 1)),
          ref: focusTarget,
          size: "small",
          variant: "text",
          children: [
            "← ",
            t("Prev")
          ]
        }
      ) }),
      /* @__PURE__ */ jsx(EzHeading, { size: "5", as: "h2", children: focusedDate.format("MMMM YYYY") }),
      /* @__PURE__ */ jsx("div", { className: pagination(), children: /* @__PURE__ */ jsxs(
        EzButton,
        {
          color: "primary",
          onClick: () => setFocusedDate(focusedDate.add(1, "month").set("date", 1)),
          size: "small",
          variant: "text",
          children: [
            t("Next"),
            " →"
          ]
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx("div", { className: row(), children: repeat(weekDayCount).map((_, dayIndex) => {
      const day = focusedDate.set("day", dayIndex);
      return /* @__PURE__ */ jsx("div", { className: cell(), children: /* @__PURE__ */ jsx("abbr", { className: dayOfWeek(), title: day.format("dddd"), children: day.format("dd") }) }, dayIndex);
    }) }),
    /* @__PURE__ */ jsx("div", { children: populateMonth(focusedDate).map((week, weekIndex) => /* @__PURE__ */ jsx("div", { className: row(), children: week.map((day, dayIndex) => {
      const currentDay = focusedDate.set("date", day);
      const selectDate = () => onChange(currentDay.format(t("DATE_FORMAT")));
      const disabled = !isEnabled(currentDay);
      const selected = currentDay.isSame(selectedDate);
      return /* @__PURE__ */ jsx("div", { className: cell({ selected }), children: day && /* @__PURE__ */ jsx(
        "button",
        {
          ref: refs[day - 1],
          "aria-label": currentDay.format("dddd, MMMM D, YYYY").toString(),
          className: dayValue(),
          disabled,
          onClick: selectDate,
          onKeyDown: handleKeyInput(selectDate, disabled),
          tabIndex: day === focusedDate.date() ? 0 : -1,
          type: "button",
          children: /* @__PURE__ */ jsx(EzTextStyle, { use: disabled ? "subdued" : void 0, children: day })
        }
      ) }, dayIndex);
    }) }, weekIndex)) })
  ] });
};
const EzCalendar$1 = forwardRef(EzCalendar);
export {
  EzCalendar$1 as default
};
//# sourceMappingURL=EzCalendar.es.js.map
